import { useCallback } from 'react';
// Using Download icon for the button
import { Download } from 'lucide-react';
import { useState, useContext, useEffect } from "react";
import { motion } from "framer-motion";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import Preloader from "./Preloader";
import { MdPhoneIphone,MdAndroid } from "react-icons/md";
import { AuthContext } from "../context/AuthContext";
import { useLocation } from 'react-router-dom';

// Define the custom hook
export const usePwaInstall = () => {
  // State to hold the event object needed to trigger the prompt
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  // State to track if the app is running in standalone mode
  const [isInStandaloneMode, setIsInStandaloneMode] = useState(false);
  // State to track if the device is iOS
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // --- Standalone Mode Detection ---
    const checkStandalone = () => {
      const isStandardStandalone = window.matchMedia('(display-mode: standalone)').matches;
      // Check for iOS standalone mode (less reliable but included for completeness)
      const isIOSStandalone = 'standalone' in window.navigator && window.navigator.standalone === true;
      setIsInStandaloneMode(isStandardStandalone || isIOSStandalone);
    };

    // --- iOS Detection ---
    const checkIOS = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      setIsIOS(/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream);
    };

    // Run checks
    checkStandalone();
    checkIOS();

    // --- Event Listener for Install Prompt ---
    const handleBeforeInstallPrompt = (event) => {
      // Prevent the mini-infobar from appearing on mobile (default browser behavior)
      event.preventDefault();
      // Stash the event so it can be triggered later.
      setInstallPromptEvent(event);
      console.log('\'beforeinstallprompt\' event captured by hook.');
    };

    // Add the event listener only if not already installed
    if (!isInStandaloneMode) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      console.log('Hook added \'beforeinstallprompt\' event listener.');
    } else {
      console.log('Hook: App is already in standalone mode.');
    }

    // Cleanup function to remove the listener when the component unmounts
    return () => {
      if (!isInStandaloneMode) {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        console.log('Hook removed \'beforeinstallprompt\' event listener.');
      }
    };
    // Run this effect only once on mount
  }, [isInStandaloneMode]); // Re-run if standalone mode changes (unlikely but possible)

  // --- Function to Trigger the Prompt ---
  const triggerInstallPrompt = useCallback(async () => {
    if (!installPromptEvent) {
      console.log('Hook: Install prompt event not available.');
      // Optionally inform the user, especially on iOS where it's expected not to work
      if (isIOS) {
         alert("To install this app on iOS, please tap the Share icon and then 'Add to Home Screen'.");
      } else {
         alert("Installation prompt isn't available right now. The app might already be installed or the browser doesn't support it.");
      }
      return false; // Indicate failure or unavailability
    }

    try {
      // Show the browser's installation prompt.
      installPromptEvent.prompt(); // Call prompt() on the event object

      // Wait for the user to respond to the prompt
      const { outcome } = await installPromptEvent.userChoice;
      console.log(`Hook: User response to the install prompt: ${outcome}`);

      // We've used the prompt, it can't be used again. Clear the state.
      setInstallPromptEvent(null);

      return outcome === 'accepted'; // Return true if accepted
    } catch (error) {
       console.error('Hook: Error triggering install prompt:', error);
       setInstallPromptEvent(null); // Clear on error too
       return false; // Indicate failure
    }
  }, [installPromptEvent, isIOS]); // Dependencies for useCallback

  // Return the state and the trigger function
  return {
    canInstall: !!installPromptEvent && !isInStandaloneMode, // Prompt available and not installed
    triggerInstallPrompt,
    isIOS,
    isInStandaloneMode,
  };
};

// Define the PwaInstallButton component
const PwaInstallButton = () => {
  // Use the custom hook to get state and functions
  const { canInstall, triggerInstallPrompt, isIOS, isInStandaloneMode } = usePwaInstall();

  // --- Render Logic ---

  // If already installed, show a message
  if (isInStandaloneMode) {
    return <p className="text-center text-green-600 text-sm mt-4">App is already installed!</p>;
  }

  // If on iOS and install isn't possible via button, show instructions
  // (We check canInstall because even on iOS, we might theoretically get the event, though prompt() won't work)
   if (isIOS && !canInstall) {
     return (
       <div className="text-center p-3 bg-gray-100 border border-gray-300 rounded-md text-sm text-gray-700 mt-4">
         On iOS, install by tapping Share {'->'} Add to Home Screen.
       </div>
     );
   }

  // If the prompt is available (canInstall is true), show the button
  if (canInstall) {
    return (
      <div className="flex justify-center mt-6">
        <button
          onClick={triggerInstallPrompt} // Use the function directly from the hook
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          aria-label="Install web application"
        >
          <Download size={20} className="mr-2 -ml-1" />
          Install App
        </button>
      </div>
    );
  }

  // Otherwise (e.g., on desktop, or before the event fires, or unsupported browser), render nothing
  return null;
};


function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function Login() {
  const [preloader, setpreloader] = useState(false);
  const [username, setusername] = useState("");
  const [password, setpassword] = useState("");
  const query = useQuery();
  // Refer link: /login?ref=CODE pre-fills the referral code for new users
  const [refferal_code, setrefferal_code] = useState(query.get('ref') || "");
  const [ButtonName, setButtonName] = useState("Send OTP");
  const [OtpContainer, setOtpContainer] = useState(false);
  const [isIphone, setIsIphone] = useState(false);
  const [RefferalContainer, setRefferalContainer] = useState(false);
  const agentId = query.get('agent');
  const { isAuthenticated, login, TelegramMessageSupport } =
    useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated]);
  const message = (status, text) => {
    if (status === 1) {
      toast.success(text, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } else if (status === 0) {
      toast.error(text, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } else if (status === 2) {
      toast.warning(text, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
  };
  
  const handleInstallClick = () => {
    
  }
  const handleLogin = () => {
    if (username === "") {
      message(0, "Please enter Mobile Number.");
      return;
    }
    if (!OtpContainer) {
      let data = {
        mobile: username,
      };
      setpreloader(true);
      login(data, function (response) {
        if (response.status === 0) {
          setpreloader(false);
          message(0, response.msg);
        } else {
          setOtpContainer(true);
          setButtonName("Verify OTP & Login");
          setpreloader(false);
          message(1, response.msg);
        }
        // Refer code field is shown on every login (new and existing users); it stays optional
        if (response.status !== 0) {
          setRefferalContainer(true);
        }
      });
      return;
    } else {
      if (password === "") {
        message(0, "Please enter OTP.");
        return;
      } else {
        let data = {
          mobile: username,
          otp: password,
          refferal: refferal_code
        };
        if(agentId){
          data['agentId'] = agentId
        }
        setpreloader(true);
        login(data, function (response) {
          if (response.status === 0) {
            setpreloader(false);
            message(0, response.msg);
          } else {
            setpreloader(false);
            message(1, response.msg);
          }
        });
        return;
      }
    }
  };

  if (isAuthenticated) {
    navigate("/dashboard");
  }

  useEffect(() => {
    // Access the browser's user agent string
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    // Check if the user agent string contains "iPhone"
    // Using a case-insensitive regex check (/iPhone/i) is generally robust
    if (/iPhone/i.test(userAgent)) {
      setIsIphone(true); // Update state if it's an iPhone
    } else {
      setIsIphone(false); // Update state if it's not an iPhone
    }

    // The empty dependency array [] ensures this effect runs only once on mount
  }, []);
  return (
    <>
      <Preloader status={preloader} />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <motion.div
        initial={{ translateX: -100 }}
        animate={{ translateX: 0 }}
        exit={{ translateX: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className="aghorilogocontainer">
          <img src="/favicon.png" className="aghorilogo" alt="Logo" />
        </div>
        <p className="texttitle">AGHORI MATKA</p>
        <div className="recharge_box">
          <label className="custom-field one">
            <input
              type="tel"
              onInput={(event) => {
                setusername(event.target.value);
              }}
              placeholder="&nbsp;"
            />
            <span className="placeholder">Enter Phone Number</span>
            <span className="border"></span>
          </label>
          {RefferalContainer && (
            <label className="custom-field one">
              <input
                type="tel"
                onInput={(event) => {
                  setrefferal_code(event.target.value);
                }}
                placeholder="&nbsp;"
                value={refferal_code}
              />
              <span className="placeholder">Refferal Code (optional)</span>
              <span className="border"></span>
            </label>
          )}
          {OtpContainer ? (
            <label className="custom-field one">
            <input
              type="tel"
              onInput={(event) => {
                setpassword(event.target.value);
              }}
              placeholder="&nbsp;"
            />
            <span className="placeholder">OTP</span>
            <span className="border"></span>
          </label>
          ) : (
            ""
          )}
          <div className="input_box_btn">
            <button className="login_btn ripple" onClick={handleLogin}>
              {ButtonName}
            </button>
          </div>
          { !isIphone ? 
          <div className="input_box_btn">
            <button
              className="login_btn ripple"
              onClick={() => {
                window.open('https://aghorimatka.com/');
              }}
            >
              Install <MdAndroid/> Android Application
            </button>
          </div>
          : ''}
          { isIphone ? 
          <div className="input_box_btn">
            <button
              className="login_btn ripple"
              onClick={()=>{handleInstallClick()}}
            >
              Install <MdPhoneIphone/> iPhone Application
            </button>
          </div>
          : '' }
        </div>
      </motion.div>
    </>
  );
}

export default Login;
